import type { EditorSelectionState } from "@/commands/Command";
import { selectionEqual } from "@/editor/shell/shellSelection";
import { StoreSubject } from "@/lib/store/StoreSubject";
import {
  stringArrayStateEqual,
  type EditorToolState,
} from "./editorStoreTypes";

export function createDefaultEditorSelection(): EditorSelectionState {
  return {
    selectedNodeIds: [],
    primarySelectedNodeId: undefined,
    mode: "select",
    selectedVertexIds: [],
    selectedEdgeIds: [],
  };
}

function toolStateEqual(left: EditorToolState, right: EditorToolState): boolean {
  return left.activeTool === right.activeTool && left.selectedShapeKind === right.selectedShapeKind;
}

export class SelectionStore {
  readonly selection = new StoreSubject<EditorSelectionState>(createDefaultEditorSelection(), {
    equals: selectionEqual,
  });

  readonly tool = new StoreSubject<EditorToolState>({
    activeTool: "select",
    selectedShapeKind: "rectangle",
  }, {
    equals: toolStateEqual,
  });

  readonly freshCutVertexIds = new StoreSubject<string[]>([], {
    equals: stringArrayStateEqual,
  });

  readonly selectedObjectConnectionKeys = new StoreSubject<string[]>([], {
    equals: stringArrayStateEqual,
  });

  getSelection = (): EditorSelectionState => {
    return this.selection.getValue();
  };

  setSelection = (selection: EditorSelectionState): void => {
    this.selection.next(selection);
  };

  patchSelection = (patch: Partial<EditorSelectionState>): void => {
    this.setSelection({ ...this.getSelection(), ...patch });
  };

  setActiveTool = (activeTool: EditorToolState["activeTool"]): void => {
    const current = this.tool.getValue();
    if (current.activeTool === activeTool) {
      return;
    }
    this.tool.next({ ...current, activeTool });
  };

  setSelectedShapeKind = (selectedShapeKind: EditorToolState["selectedShapeKind"]): void => {
    const current = this.tool.getValue();
    if (current.selectedShapeKind === selectedShapeKind) {
      return;
    }
    this.tool.next({ ...current, selectedShapeKind });
  };

  setFreshCutVertexIds = (vertexIds: string[]): void => {
    this.freshCutVertexIds.next(vertexIds);
  };

  updateFreshCutVertexIds = (updater: (vertexIds: string[]) => string[]): void => {
    this.setFreshCutVertexIds(updater(this.freshCutVertexIds.getValue()));
  };

  setSelectedObjectConnectionKeys = (keys: string[]): void => {
    this.selectedObjectConnectionKeys.next(keys);
  };
}

export const selectionStore = new SelectionStore();
